import { ExpandableChart, StrategyChart } from "@scryerlabs/ui/charts";
import { CandleChartPanel } from "./CandleChartPanel";
import { AnnualisedReturnsTable } from "./AnnualisedReturnsTable";
import { TradesTable } from "./TradesTable";
import { BacktestNote } from "./BacktestNote";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CCol,
  CNav,
  CNavItem,
  CNavLink,
  CRow,
  CSpinner,
} from "@coreui/react-pro";
import {
  chartDurationMonths,
  formatChartPeriod,
  TradingStrategyChartType,
  TradingStrategyStatTone,
  useTradingStrategyChartContent,
} from "../../../api/hooks/useTradingStrategies";
import type { TradingStrategyDto } from "../../../api/hooks/useTradingStrategies";

interface TradingStrategyInsightsProps {
  strategy: TradingStrategyDto;
  /** Name the backtest window in the note at the foot, for a page that doesn't show it elsewhere. */
  periodInNote?: boolean;
  /** Off where the page shows StrategyFigures itself, higher up. */
  showFigures?: boolean;
}

/**
 * Tiles across the top of the detail page. Six fills the row at md without wrapping, and covers the
 * nine's headline three plus best year, worst year and profitable years.
 *
 * The back office preview shows the same number deliberately — a preview that disagrees with the
 * page it previews is worse than no preview.
 */
const DETAIL_METRIC_COUNT = 6;

function toneColorClass(tone: TradingStrategyStatTone): string {
  switch (tone) {
    case TradingStrategyStatTone.Positive:
      return "text-success";
    case TradingStrategyStatTone.Negative:
      return "text-danger";
    default:
      return "";
  }
}

/**
 * The first six figures by display order, not the highlighted ones, as tiles: one row of six across
 * the page, or two rows of three where they sit in a column beside something else.
 *
 * A reader on a strategy's page has already committed to looking, so it can afford more than the
 * three a card leads with — and how many tiles fit above a chart is this layout's business, not
 * something the data should be asked to encode. Reading off the same ordering the card does means an
 * admin reordering the stats moves both surfaces together.
 *
 * The rest are not lost: the Backtested Stats tab lists every one of them.
 */
export function StrategyFigures({
  strategy,
  threeAcross = false,
}: {
  strategy: TradingStrategyDto;
  threeAcross?: boolean;
}) {
  const metrics = useMemo(
    () =>
      [...strategy.stats]
        .filter((s) => s.showOnFrontOffice)
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .slice(0, DETAIL_METRIC_COUNT),
    [strategy.stats],
  );

  if (metrics.length === 0) {
    return null;
  }

  return (
    <CRow xs={{ gutter: 3 }} className={threeAcross ? "mt-1" : "mt-4"}>
      {metrics.map((m) => (
        <CCol key={`${m.label}-${m.displayOrder}`} xs={6} md={threeAcross ? 4 : 2}>
          <MetricBox
            label={m.label}
            value={m.value}
            colorClass={toneColorClass(m.tone)}
            compact={threeAcross}
          />
        </CCol>
      ))}
    </CRow>
  );
}

export function TradingStrategyInsights({
  strategy,
  periodInNote = false,
  showFigures = true,
}: TradingStrategyInsightsProps) {
  const { t } = useTranslation("tradingStrategies");
  const performanceNote = periodInNote
    ? t("performanceNoteWithPeriod", {
        period: formatChartPeriod(strategy),
        months: chartDurationMonths(strategy),
      })
    : t("performanceNote");
  const sortedStats = useMemo(
    () =>
      [...strategy.stats]
        .filter((s) => s.showOnFrontOffice)
        .sort((a, b) => a.displayOrder - b.displayOrder),
    [strategy.stats],
  );
  const sortedCharts = useMemo(
    () =>
      [...strategy.charts]
        .filter((c) => c.chartType !== TradingStrategyChartType.Brochure)
        .sort((a, b) => a.displayOrder - b.displayOrder),
    [strategy.charts],
  );
  const firstChartTab = sortedCharts[0]?.id ?? "stats";

  const annualisedReturns = useMemo(
    () => strategy.annualisedReturns ?? [],
    [strategy.annualisedReturns],
  );
  const trades = useMemo(() => strategy.trades ?? [], [strategy.trades]);
  const [activeTab, setActiveTab] = useState(firstChartTab);

  useEffect(() => {
    setActiveTab(firstChartTab);
  }, [firstChartTab]);

  return (
    <>
      {showFigures && <StrategyFigures strategy={strategy} />}

      {/* Only the chart tabs are conditional: every strategy has stats, returns and trades, so
          those three are fixtures with an empty state inside rather than a tab strip that changes
          shape between strategies. */}
      <CNav variant="tabs" className="mt-4 strategy-insight-tabs">
        {sortedCharts.map((chart) => (
          <CNavItem key={chart.id}>
            <CNavLink
              href="#"
              active={activeTab === chart.id}
              onClick={(e) => {
                e.preventDefault();
                setActiveTab(chart.id);
              }}
            >
              {chart.name}
            </CNavLink>
          </CNavItem>
        ))}
        <CNavItem>
          <CNavLink
            href="#"
            active={activeTab === "stats"}
            onClick={(e) => {
              e.preventDefault();
              setActiveTab("stats");
            }}
          >
            Backtested Stats
          </CNavLink>
        </CNavItem>
        <CNavItem>
          <CNavLink
            href="#"
            active={activeTab === "returns"}
            onClick={(e) => {
              e.preventDefault();
              setActiveTab("returns");
            }}
          >
            Annualised Returns
          </CNavLink>
        </CNavItem>
        <CNavItem>
          <CNavLink
            href="#"
            active={activeTab === "trades"}
            onClick={(e) => {
              e.preventDefault();
              setActiveTab("trades");
            }}
          >
            Trades
          </CNavLink>
        </CNavItem>
      </CNav>

      <div className="pt-4" style={{ minHeight: 360 }}>
        {activeTab === "stats" ? (
          <BacktestedStatsList stats={sortedStats} />
        ) : activeTab === "returns" ? (
          <AnnualisedReturnsTable rows={annualisedReturns} />
        ) : activeTab === "trades" ? (
          <TradesTable trades={trades} />
        ) : (
          <StrategyChartTab
            strategyId={strategy.id}
            chartId={activeTab}
            chartName={
              sortedCharts.find((chart) => chart.id === activeTab)?.name ??
              "Chart"
            }
          />
        )}
      </div>

      <BacktestNote text={performanceNote} />
    </>
  );
}

export function LabelledFact({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: React.ReactNode;
  /** For a value that is an identifier rather than a phrase, and gets read character by character. */
  mono?: boolean;
}) {
  return (
    <>
      <div
        className="text-body-secondary text-uppercase mb-1"
        style={{
          letterSpacing: "0.08em",
          fontSize: "0.7rem",
          fontWeight: 600,
        }}
      >
        {label}
      </div>
      <div
        className={mono ? undefined : "fw-semibold"}
        style={
          mono
            ? {
                fontFamily: "var(--sl-font-mono)",
                fontSize: "0.85rem",
                wordBreak: "break-word",
              }
            : { fontSize: "0.95rem" }
        }
      >
        {value}
      </div>
    </>
  );
}

function MetricBox({
  label,
  value,
  colorClass,
  compact = false,
}: {
  label: string;
  value: string;
  colorClass: string;
  /** Three across a column is narrower than six across the page, so the figure steps down a size. */
  compact?: boolean;
}) {
  return (
    <div
      className={`border rounded ${compact ? "p-2" : "p-3"} text-center h-100 bg-body-tertiary d-flex flex-column justify-content-center`}
      style={{ minHeight: compact ? 66 : 78 }}
    >
      <div
        className={`fw-bold ${colorClass}`}
        style={{ fontSize: compact ? "1.15rem" : "1.4rem", lineHeight: 1.1 }}
      >
        {value}
      </div>
      <div
        className="text-body-secondary text-uppercase mt-1"
        style={{ fontSize: "0.65rem", letterSpacing: "0.06em" }}
      >
        {label}
      </div>
    </div>
  );
}

/**
 * Draws a strategy's chart from the payload the back office published.
 *
 * Fetched per chart rather than with the strategy, and rendered by the shared StrategyChart so the
 * front office, the back office preview and the brochure all draw the same payload the same way.
 */
function StrategyChartTab({
  strategyId,
  chartId,
  chartName,
}: {
  strategyId: string;
  chartId: string;
  chartName: string;
}) {
  const { t } = useTranslation("tradingStrategies");
  const { data, isLoading, isError } = useTradingStrategyChartContent(
    strategyId,
    chartId,
  );

  if (isLoading) {
    return (
      <div
        className="border rounded bg-body-tertiary d-flex align-items-center justify-content-center p-4"
        style={{ minHeight: 320 }}
      >
        <CSpinner size="sm" />
      </div>
    );
  }

  // A chart whose content has not arrived yet, or whose blob has gone. The strategy still reads, so
  // this says so quietly rather than looking like a broken page.
  if (isError || !data) {
    return (
      <div
        className="border rounded bg-body-tertiary d-flex align-items-center justify-content-center text-center p-4"
        style={{ minHeight: 320 }}
      >
        <div>
          <div className="fw-semibold mb-1">{chartName}</div>
          <div className="text-body-secondary small">
            This chart is not available yet.
          </div>
        </div>
      </div>
    );
  }

  // Candles have their own renderer, loaded on demand. Chart.js draws every other kind.
  return (
    <div className="border rounded bg-body-tertiary p-3">
      {data.chartType === "candlestick" ? (
        <CandleChartPanel payload={data} title={chartName} height={340} />
      ) : (
        <ExpandableChart
          title={chartName}
          height={340}
          expandLabel={t("charts.expand")}
          closeLabel={t("charts.close")}
        >
          {(height) => <StrategyChart payload={data} height={height} />}
        </ExpandableChart>
      )}
    </div>
  );
}

function BacktestedStatsList({
  stats,
}: {
  stats: TradingStrategyDto["stats"];
}) {
  if (stats.length === 0) {
    return (
      <div className="text-body-secondary text-center py-5">
        No backtested stats have been published for this strategy.
      </div>
    );
  }

  return (
    <CRow xs={{ gutter: 2 }}>
      {stats.map((row) => (
        <CCol key={`${row.label}-${row.displayOrder}`} xs={12} md={6}>
          <div className="d-flex justify-content-between p-3 border rounded bg-body-tertiary">
            <span className="text-body-secondary">{row.label}</span>
            <span className={`fw-bold ${toneColorClass(row.tone)}`}>
              {row.value}
            </span>
          </div>
        </CCol>
      ))}
    </CRow>
  );
}
